import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

import type { Highlight } from '../../api/types'
import { ApiError, fehlertext } from '../../api/client'
import { SprechblaseIcon } from '../../components/Icons'
import { Standbild } from '../../components/Standbild'
import { useEscape } from '../../hooks/useEscape'
import { useHighlights } from '../../hooks/queries'
import { useSendKey } from '../../hooks/useSendKey'
import { deckblattVon } from '../../lib/deckblatt'
import { datumKurz } from '../../lib/ortszeit'
import { standbildAdresse } from '../../lib/video'
import { useTexte } from '../../texte'
import { Blattkopf } from '../../components/Blattkopf'

/** As many as the server takes in one post (`HighlightsController::LINKS_MAX`). */
export const KARTENLINK_MAX = 20

/**
 * "Link zur Karte" (1.113.0): points already on the map, chosen for a post
 * with their cards -- the post a publishing in the workbench writes, for
 * points that are published already.
 *
 * **Only published points, newest first.** A draft linked from the chat would
 * be a draft shown to everyone; the server refuses it as well, and this list
 * never offers it.
 *
 * **The mark says where a point stands already** (`in_chat`, from the
 * server): a point may stand in several posts, and the author sees before
 * choosing that this one is in the chat.
 *
 * **Head and foot stand, the list scrolls** (`bogen__blatt--gegliedert`,
 * from use: "stehende Kopf und Fußzeilen"). At the page body, like every
 * sheet here.
 */
export function Kartenlinkwahl({
  tripId,
  ort,
  onSenden,
  onZu,
}: {
  tripId: number
  /** Where the post goes (`sendeOrt`), part of its key. */
  ort: string
  /** Throws when the server refuses -- the sheet stays with its choice. */
  onSenden: (eingabe: { ids: number[]; client_key: string }) => Promise<void>
  onZu: () => void
}) {
  const t = useTexte()
  const tk = t.chat.kartenlink
  const punkte = useHighlights(tripId, true)
  const [gewaehlt, setGewaehlt] = useState<number[]>([])
  const [laeuft, setLaeuft] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const sendeSchluessel = useSendKey(ort)
  useEscape(onZu)

  const liste = useMemo(
    () =>
      (punkte.data?.highlights ?? [])
        .filter((h) => h.published_at !== null)
        .sort((a, b) => zeitVon(b).localeCompare(zeitVon(a)) || b.id - a.id),
    [punkte.data],
  )

  function umschalten(id: number) {
    setProblem(null)
    setGewaehlt((alt) => {
      if (alt.includes(id)) return alt.filter((x) => x !== id)
      if (alt.length >= KARTENLINK_MAX) {
        setProblem(tk.hoechstens(KARTENLINK_MAX))
        return alt
      }
      return [...alt, id]
    })
  }

  async function sende(): Promise<void> {
    setProblem(null)
    setLaeuft(true)
    // In the order of the list, so the line names them as the list does.
    const ids = liste.map((h) => h.id).filter((id) => gewaehlt.includes(id))
    try {
      await onSenden({ ids, client_key: sendeSchluessel.keyFor(JSON.stringify(ids)) })
      onZu()
    } catch (fehler) {
      setProblem(
        fehler instanceof ApiError && fehler.offline
          ? tk.ohneNetz
          : fehler instanceof ApiError
            ? fehlertext(fehler)
            : fehler instanceof Error
              ? fehler.message
              : t.allgemein.schiefgelaufen,
      )
    } finally {
      setLaeuft(false)
    }
  }

  return createPortal(
    <div className="bogen" role="dialog" aria-modal="true" aria-label={tk.titel} data-kartenlink="">
      <div className="bogen__blatt bogen__blatt--gegliedert">
        <Blattkopf titel={tk.titel} onSchliessen={onZu} />

        <div className="bogen__rolle">
          {punkte.isPending ? (
            <p className="bogen__hinweis">{tk.laedt}</p>
          ) : liste.length === 0 ? (
            <p className="bogen__hinweis" data-kartenlink-leer="">{tk.leer}</p>
          ) : (
            <ul className="kartenlinkwahl__liste">
              {liste.map((h) => (
                <li key={h.id}>
                  <Zeile highlight={h} an={gewaehlt.includes(h.id)} onUmschalten={() => umschalten(h.id)} imChat={tk.imChat} />
                </li>
              ))}
            </ul>
          )}
          {problem !== null && (
            <p className="bogen__problem" role="alert" data-kartenlink-fehler="">
              {problem}
            </p>
          )}
        </div>

        <div className="bogen__fuss">
          <button
            type="button"
            className="button"
            disabled={gewaehlt.length === 0 || laeuft}
            onClick={() => void sende()}
            data-kartenlink-senden=""
          >
            {tk.hinzufuegen(gewaehlt.length)}
          </button>
          <button type="button" className="button button--outline" onClick={onZu}>
            {t.allgemein.abbrechen}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function zeitVon(h: Highlight): string {
  return h.happened_at ?? h.created_at
}

function Zeile({
  highlight,
  an,
  onUmschalten,
  imChat,
}: {
  highlight: Highlight
  an: boolean
  onUmschalten: () => void
  imChat: string
}) {
  const deckblatt = deckblattVon(highlight)
  const bild = deckblatt === undefined ? null : standbildAdresse(deckblatt)
  return (
    <label
      className="kartenlinkwahl__zeile"
      data-kartenlink-zeile={highlight.id}
      data-im-chat={highlight.in_chat === true ? '' : undefined}
    >
      <input type="checkbox" className="kartenlinkwahl__haken" checked={an} onChange={onUmschalten} />
      <span className="kartenlinkwahl__bild">
        {bild !== null && <Standbild src={bild} loading="lazy" />}
      </span>
      <span className="kartenlinkwahl__name">
        {highlight.title}
        <span className="kartenlinkwahl__wann">{datumKurz(zeitVon(highlight), highlight.tz_offset_min)}</span>
      </span>
      {highlight.in_chat === true && (
        <span className="kartenlinkwahl__marke" title={imChat}>
          <SprechblaseIcon size={18} />
          <span className="visually-hidden">{imChat}</span>
        </span>
      )}
    </label>
  )
}
